import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { hasPermission } from "../lib/permissions";
import { canInitiateAssist } from "../lib/assist";
import InviteDialog from "../components/assist/InviteDialog";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowDownLeft, ArrowLeft, ArrowUpRight, Clock, Loader2, MoreHorizontal, Paperclip, RefreshCw, StickyNote, X,
} from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  CATEGORY_LABELS, CAUSE_LABELS, CONTACT_CHANNEL_LABELS, channelLabel, DELAY_TONES, PRIORITY_LABELS, SATISFACTION_LABELS, delayText, formatDateTime,
  announceTicketsChanged, localInputToIso, localNowInput, primaryAction, statusMoves, systemEntryText, ticketErrorMessage,
} from "../lib/tickets";
import { PriorityBadge, TicketStatusBadge } from "../components/tickets/TicketBits";
import { AssignDialog, AttachPharmacyDialog, EditTicketDialog, PriorityDialog, StatusDialog } from "../components/tickets/TicketDialogs";
import TicketLinksCard, { SatisfactionDialog } from "../components/tickets/TicketLinks";
import TicketProductCard from "../components/product/TicketProductCard";
import { newKey } from "../components/equipment/EquipmentDialogs";
import { ReceiptPreviewDialog } from "../components/finance/ReceiptPreview";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuSeparator as DropdownMenuSeparator, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

function Delay({ label, measure, testid }) {
  if (!measure) return null;
  return (
    <div className="flex items-center justify-between gap-3 text-sm" data-testid={testid}>
      <span className="text-slate-500">{label}</span>
      <span className={`tabular-nums font-medium ${DELAY_TONES[measure.state]}`}>{delayText(measure)}</span>
    </div>
  );
}

function SideRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm py-1.5">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-right text-slate-900 min-w-0 break-words">{children}</span>
    </div>
  );
}

function Entry({ entry, names, onPreview }) {
  if (entry.kind === "system") {
    return (
      <li className="flex gap-3 text-sm text-slate-500" data-testid="ticket-entry-system">
        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <span className="text-slate-700">{systemEntryText(entry, (id) => names[id] || "un compte")}</span>
          <span className="text-xs"> · {entry.author_name} · {formatDateTime(entry.created_at)}</span>
          {entry.body && <p className="text-slate-600 mt-0.5 whitespace-pre-line">« {entry.body} »</p>}
          {entry.data?.solution && <p className="text-slate-600 mt-0.5 whitespace-pre-line">Solution : {entry.data.solution}</p>}
        </div>
      </li>
    );
  }
  const contact = entry.kind === "contact";
  const DirectionIcon = entry.direction === "in" ? ArrowDownLeft : ArrowUpRight;
  return (
    <li className={`rounded-lg border p-3 ${contact ? "border-slate-200 bg-white" : "border-amber-200 bg-amber-50/60"}`} data-testid={`ticket-entry-${entry.kind}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
        {contact ? (
          <span className="inline-flex items-center gap-1 font-medium text-slate-700">
            <DirectionIcon size={13} aria-hidden="true" />
            {entry.direction === "in" ? "Reçu de la pharmacie" : "Envoyé à la pharmacie"} · {CONTACT_CHANNEL_LABELS[entry.channel] || entry.channel}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 font-medium text-amber-800"><StickyNote size={13} aria-hidden="true" />Note interne</span>
        )}
        <span>{entry.author_name} · {formatDateTime(contact ? entry.occurred_at : entry.created_at)}</span>
        {entry.corrects_entry_id && <span className="italic">corrige une entrée précédente</span>}
      </div>
      <p className="text-sm text-slate-800 mt-1.5 whitespace-pre-line break-words">{entry.body}</p>
      {entry.files?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {entry.files.map((f) => (
            <button key={f.id} type="button" className="inline-flex items-center gap-1 text-xs text-jamora hover:underline" onClick={() => onPreview(f)}>
              <Paperclip size={12} aria-hidden="true" />{f.name}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}

// Note interne / Échange avec la pharmacie (D-20, D-21): nothing is sent to the pharmacy, the agent RECORDS what was said.
function Composer({ ticket, mode, setMode, onSaved, composerRef }) {
  const { user } = useAuth();
  const canContact = !!ticket.capabilities?.manage;
  const [body, setBody] = useState("");
  const [channel, setChannel] = useState("phone");
  const [direction, setDirection] = useState("out");
  const [when, setWhen] = useState(localNowInput);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const input = useRef(null);
  const filesOn = !!user?.features?.collaborators; // private files need the replica-set modules (files/config.py)
  const effectiveMode = canContact ? mode : "note";

  const upload = async (picked) => {
    if (!picked.length) return;
    setUploading(true);
    try {
      for (const file of picked) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/files", form, { params: { context: "ticket_entry" }, headers: { "Idempotency-Key": newKey() } });
        setFiles((list) => [...list, { id: data.id, name: data.name || file.name }]);
      }
    } catch (e) {
      toast.error(ticketErrorMessage(e, "Fichier refusé : image (JPEG, PNG, WebP) ou PDF de 10 Mo maximum."));
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
  };

  const send = async () => {
    if (!body.trim() || saving) return;
    setSaving(true);
    try {
      const payload = { kind: effectiveMode, body: body.trim(), file_ids: files.map((f) => f.id) };
      if (effectiveMode === "contact") Object.assign(payload, { channel, direction, occurred_at: localInputToIso(when) });
      const { data } = await api.post(`/tickets/${ticket.id}/entries`, payload);
      setBody(""); setFiles([]); setWhen(localNowInput());
      onSaved(data, effectiveMode === "contact" ? "Échange consigné" : "Note ajoutée");
    } catch (e) {
      toast.error(ticketErrorMessage(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4 space-y-3" ref={composerRef} data-testid="ticket-composer">
      {canContact && (
        <div className="flex gap-1" role="tablist" aria-label="Type d'entrée">
          {[["contact", "Échange avec la pharmacie"], ["note", "Note interne"]].map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={mode === id} onClick={() => setMode(id)} data-testid={`ticket-composer-${id}`}
              className={`flex-1 sm:flex-none rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${mode === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
              {label}
            </button>
          ))}
        </div>
      )}
      {effectiveMode === "contact" && (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="ticket-contact-direction">Sens</Label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger id="ticket-contact-direction" data-testid="ticket-contact-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="out">Nous avons contacté la pharmacie</SelectItem>
                <SelectItem value="in">La pharmacie nous a contactés</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ticket-contact-channel">Canal</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger id="ticket-contact-channel" data-testid="ticket-contact-channel"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CONTACT_CHANNEL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ticket-contact-when">Quand</Label>
            <Input id="ticket-contact-when" type="datetime-local" value={when} max={localNowInput()} onChange={(e) => setWhen(e.target.value)} />
          </div>
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="ticket-entry-body" className="sr-only">{effectiveMode === "contact" ? "Résumé de l'échange" : "Note interne"}</Label>
        <Textarea id="ticket-entry-body" value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={5000} data-testid="ticket-entry-body"
          placeholder={effectiveMode === "contact" ? "Ce qui a été dit ou demandé" : "Visible seulement par l'équipe"} />
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((f) => (
            <span key={f.id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
              <Paperclip size={12} aria-hidden="true" />{f.name}
              <button type="button" aria-label={`Retirer ${f.name}`} onClick={() => setFiles((l) => l.filter((x) => x.id !== f.id))}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        {filesOn ? (
          <>
            <input ref={input} type="file" className="hidden" accept="image/jpeg,image/png,image/webp,application/pdf" multiple
              onChange={(e) => upload(Array.from(e.target.files || []))} />
            <Button type="button" variant="ghost" size="sm" onClick={() => input.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Paperclip size={14} className="mr-1.5" />}Joindre
            </Button>
          </>
        ) : <span />}
        <Button type="button" onClick={send} disabled={!body.trim() || saving || uploading} data-testid="ticket-entry-submit">
          {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}{effectiveMode === "contact" ? "Consigner l'échange" : "Ajouter la note"}
        </Button>
      </div>
    </Card>
  );
}

export default function TicketDetail() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const [move, setMove] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [mode, setMode] = useState("contact");
  const [taking, setTaking] = useState(false);
  const [preview, setPreview] = useState(null);
  const composerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${ticketId}`);
      setTicket(data);
      setPhase("ready");
    } catch (e) {
      setErrorText(e?.response?.status === 404 ? "Ce ticket n'existe pas ou ne vous est pas accessible." : ticketErrorMessage(e, "Impossible de charger le ticket."));
      setPhase("error");
    }
  }, [ticketId]);
  useEffect(() => { setPhase("loading"); load(); }, [load]);

  const applied = (data, message) => {
    if (!data) { load(); return; } // a refused write: re-read the ticket as it is now
    setTicket(data);
    announceTicketsChanged(); // the menu's « sans responsable » count follows at once
    setMove(null);
    setDialog(null);
    if (message) toast.success(message);
  };

  const takeCharge = async () => {
    setTaking(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/assign`, { expected_version: ticket.version, responsible_user_id: user.id });
      applied(data, "Ticket pris en charge");
    } catch (e) {
      toast.error(ticketErrorMessage(e, "Impossible de prendre le ticket en charge."));
      load();
    } finally {
      setTaking(false);
    }
  };

  const focusComposer = (target) => {
    setMode(target);
    composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => document.getElementById("ticket-entry-body")?.focus(), 250);
  };

  if (phase === "loading") {
    return <div className="space-y-3" data-testid="ticket-loading"><Skeleton className="h-28 w-full" /><Skeleton className="h-48 w-full" /></div>;
  }
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="ticket-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{errorText}</p>
        <div className="flex justify-center gap-2">
          <Button variant="outline" asChild><Link to="/support/tickets">Retour aux tickets</Link></Button>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </div>
      </Card>
    );
  }

  const caps = ticket.capabilities || {};
  const primary = primaryAction(ticket);
  const moves = caps.manage ? statusMoves(ticket).filter((m) => !(primary?.kind === "status" && m.to === primary.to)) : [];
  const names = Object.fromEntries((ticket.responsible_candidates || []).map((c) => [c.id, c.name]));
  if (ticket.responsible_user_id && ticket.responsible_name) names[ticket.responsible_user_id] = ticket.responsible_name;
  const runPrimary = () => {
    if (!primary) return;
    if (primary.kind === "take_charge") takeCharge();
    else if (primary.kind === "status") setMove(statusMoves(ticket).find((m) => m.to === primary.to) || { to: primary.to, label: primary.label, needs: primary.needs });
    else focusComposer(primary.kind === "note" ? "note" : "contact");
  };
  const canProposeAssist = canInitiateAssist(user) && !ticket.to_attach;
  const isOpen = !["resolved", "closed"].includes(ticket.status);

  return (
    <div className="space-y-4" data-testid="ticket-detail">
      <Link to="/support/tickets" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Tickets</Link>

      <Card className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="text-xs text-slate-500 tabular-nums" data-testid="ticket-number">
              {ticket.number} · {ticket.to_attach ? <span>{ticket.pharmacy_name} <span className="text-amber-700">(à rattacher)</span></span> : ticket.can_open_pharmacy
                ? <Link to={`/prospects/${ticket.prospect_id}`} className="text-jamora hover:underline">{ticket.pharmacy_name}</Link>
                : ticket.pharmacy_name}
            </div>
            <h1 className="font-display font-bold text-xl text-slate-900 break-words" data-testid="ticket-title">{ticket.title}</h1>
            <div className="flex flex-wrap gap-1.5 pt-1">
              <PriorityBadge priority={ticket.priority} adjusted={ticket.priority_adjusted} />
              <TicketStatusBadge status={ticket.status} />
              {ticket.reopen_count > 0 && <span className="text-xs text-slate-500 self-center">Rouvert {ticket.reopen_count} fois</span>}
            </div>
          </div>
          {(caps.manage || moves.length > 0 || caps.satisfaction || canProposeAssist) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Autres actions" data-testid="ticket-more"><MoreHorizontal size={18} /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" title="Actions sur le ticket">
                {moves.map((m) => <DropdownMenuItem key={m.to + m.label} onClick={() => setMove(m)} data-testid={`ticket-move-${m.to}`}>{m.label}</DropdownMenuItem>)}
                {moves.length > 0 && <DropdownMenuSeparator />}
                {(ticket.responsible_candidates || []).length > 0 && ticket.status !== "closed" && (
                  <DropdownMenuItem onClick={() => setDialog("assign")} data-testid="ticket-action-assign">Changer le responsable</DropdownMenuItem>
                )}
                {canProposeAssist && <DropdownMenuItem onClick={() => setDialog("assist")} data-testid="ticket-action-assist">Proposer une assistance</DropdownMenuItem>}
                {isOpen && <DropdownMenuItem onClick={() => setDialog("priority")} data-testid="ticket-action-priority">Ajuster la priorité</DropdownMenuItem>}
                {isOpen && <DropdownMenuItem onClick={() => setDialog("edit")} data-testid="ticket-action-edit">Modifier le ticket</DropdownMenuItem>}
                {ticket.status === "new" && caps.manage && hasPermission(user, "tickets:delete") && (
                  <DropdownMenuItem onClick={() => setDialog("delete")} className="text-red-600 focus:text-red-700" data-testid="ticket-action-delete">Supprimer le ticket…</DropdownMenuItem>
                )}
                {caps.satisfaction && (
                  <DropdownMenuItem onClick={() => setDialog("satisfaction")} data-testid="ticket-action-satisfaction">
                    {ticket.satisfaction ? "Modifier la satisfaction" : "Noter la satisfaction"}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {primary && (
          <div>
            <Button onClick={runPrimary} disabled={taking} className="w-full sm:w-auto" data-testid="ticket-primary">
              {taking && <Loader2 size={14} className="mr-1.5 animate-spin" />}{primary.label}
            </Button>
          </div>
        )}
      </Card>

      {ticket.pharmacy_pending && (
        <Card className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 border-amber-200 bg-amber-50" data-testid="ticket-attach-banner">
          <AlertTriangle size={18} className="text-amber-600 shrink-0" aria-hidden="true" />
          <div className="text-sm text-amber-900 min-w-0 flex-1">
            <p className="font-medium">Demande reçue de Jamora, pharmacie à rattacher</p>
            <p>
              Envoyée par « {ticket.pharmacy_pending.display_name} »
              {ticket.pharmacy_pending.tenant_ref ? ` (Jamora : ${ticket.pharmacy_pending.tenant_ref})` : ""}
              {ticket.pharmacy_pending.suggested ? ` — suggestion : ${ticket.pharmacy_pending.suggested.name}.` : "."}
            </p>
          </div>
          {caps.attach && (
            <Button variant="outline" className="bg-white" onClick={() => setDialog("attach")} data-testid="ticket-attach">Rattacher</Button>
          )}
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-4 min-w-0">
          {(ticket.description || ticket.solution) && (
            <Card className="p-5 space-y-3">
              {ticket.description && (
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">Description</h2>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-line break-words">{ticket.description}</p>
                </div>
              )}
              {ticket.solution && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3" data-testid="ticket-solution">
                  <h2 className="text-sm font-semibold text-emerald-900">Solution{ticket.cause ? ` · ${CAUSE_LABELS[ticket.cause]}` : ""}</h2>
                  <p className="text-sm text-emerald-900 mt-1 whitespace-pre-line">{ticket.solution}</p>
                </div>
              )}
              {ticket.waiting_reason && <p className="text-sm text-violet-800" data-testid="ticket-waiting-reason">En attente : {ticket.waiting_reason}</p>}
            </Card>
          )}

          {caps.note && <Composer ticket={ticket} mode={mode} setMode={setMode} onSaved={applied} composerRef={composerRef} />}

          <Card className="p-5 space-y-3">
            <h2 className="text-sm font-semibold text-slate-900">Fil du ticket</h2>
            <ol className="space-y-3" data-testid="ticket-thread">
              {[...ticket.entries].reverse().map((e) => <Entry key={e.id} entry={e} names={names}
                onPreview={(f) => setPreview({ source: { key: `file:${f.id}`, path: `/files/${f.id}` }, name: f.name })} />)}
            </ol>
          </Card>
        </div>

        <aside className="space-y-4 min-w-0">
          <Card className="p-5 space-y-2" data-testid="ticket-delays">
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><Clock size={14} className="text-slate-400" />Délais</h2>
            <Delay label="Première réponse" measure={ticket.sla?.first_response} testid="ticket-delay-first-response" />
            <Delay label="Résolution" measure={ticket.sla?.resolution} testid="ticket-delay-resolution" />
            {!ticket.sla?.first_response && !ticket.sla?.resolution && <p className="text-sm text-slate-500">Aucun délai mesuré pour ce ticket.</p>}
          </Card>
          <TicketLinksCard ticket={ticket} onChanged={applied} />
          <TicketProductCard ticketId={ticket.id} />
          <Card className="p-5 divide-y divide-slate-100">
            <SideRow label="Responsable">{ticket.responsible_name || <span className="text-slate-400">Personne</span>}</SideRow>
            <SideRow label="Demandeur">
              {ticket.requester?.name}{ticket.requester?.role ? ` · ${ticket.requester.role}` : ""}
              {ticket.requester?.phone && <a href={`tel:${ticket.requester.phone}`} className="block text-jamora hover:underline">{ticket.requester.phone}</a>}
            </SideRow>
            <SideRow label="Catégorie">{CATEGORY_LABELS[ticket.category]}</SideRow>
            <SideRow label="Reçu par">{channelLabel(ticket.channel)}</SideRow>
            {ticket.source?.created_at && <SideRow label="Demandé dans Jamora">{formatDateTime(ticket.source.created_at)}</SideRow>}
            <SideRow label="Priorité calculée">{PRIORITY_LABELS[ticket.priority_computed]}</SideRow>
            {ticket.priority_override && (
              <SideRow label="Ajustée par">{ticket.priority_override.by_name} : « {ticket.priority_override.reason} »</SideRow>
            )}
            <SideRow label="Ouvert">{ticket.created_by_name} · {formatDateTime(ticket.created_at)}</SideRow>
            {ticket.satisfaction && (
              <SideRow label="Satisfaction">
                <span data-testid="ticket-satisfaction">{ticket.satisfaction.score}/5 · {SATISFACTION_LABELS[ticket.satisfaction.score]}</span>
                {ticket.satisfaction.comment && <span className="block text-xs text-slate-500">« {ticket.satisfaction.comment} »</span>}
                <span className="block text-xs text-slate-500">Notée par {ticket.satisfaction.by_name}</span>
              </SideRow>
            )}
          </Card>
        </aside>
      </div>

      {move && <StatusDialog ticket={ticket} move={move} onOpenChange={(open) => { if (!open) setMove(null); }} onDone={(data) => applied(data, "Statut mis à jour")} />}
      {canProposeAssist && (
        <InviteDialog open={dialog === "assist"} onOpenChange={(open) => setDialog(open ? "assist" : null)}
          initialPharmacy={ticket.pharmacy_name} initialReason={`Suite au ticket ${ticket.number}.`} />
      )}
      <AssignDialog ticket={ticket} open={dialog === "assign"} onOpenChange={(open) => setDialog(open ? "assign" : null)} onDone={(data) => applied(data, "Responsable mis à jour")} />
      <PriorityDialog ticket={ticket} open={dialog === "priority"} onOpenChange={(open) => setDialog(open ? "priority" : null)} onDone={(data) => applied(data, "Priorité mise à jour")} />
      <DeleteWithReasonDialog
        target={dialog === "delete" ? ticket : null} testId="delete-ticket" onClose={() => setDialog(null)}
        title={`Supprimer le ticket ${ticket.number} ?`}
        description="Il a été ouvert par erreur : il va dans la corbeille et se restaure pendant 30 jours. Un ticket sur lequel quelque chose s'est passé se clôt sans suite à la place."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/tickets/${ticket.id}`, { data: { reason } });
            toast.success("Ticket mis à la corbeille");
            window.dispatchEvent(new Event("tickets:changed"));
            navigate("/support/tickets");
          } catch (e) {
            toast.error(errorMessage(e, "Impossible de supprimer ce ticket."));
            setDialog(null);
          }
        }}
      />
      <EditTicketDialog ticket={ticket} open={dialog === "edit"} onOpenChange={(open) => setDialog(open ? "edit" : null)} onDone={(data) => applied(data, "Ticket modifié")} />
      {caps.satisfaction && (
        <SatisfactionDialog ticket={ticket} open={dialog === "satisfaction"} onOpenChange={(open) => setDialog(open ? "satisfaction" : null)}
          onDone={(data) => applied(data, "Satisfaction enregistrée")} />
      )}
      {caps.attach && (
        <AttachPharmacyDialog ticket={ticket} open={dialog === "attach"} onOpenChange={(open) => setDialog(open ? "attach" : null)}
          onDone={(data) => applied(data, `Ticket rattaché à ${data.pharmacy_name}`)} />
      )}
      <ReceiptPreviewDialog receipt={preview} onClose={() => setPreview(null)} description="Pièce jointe au ticket." />
    </div>
  );
}
